import { useEffect, useState } from "react";
import useStructureQueries from "hooks/useStructureQueries";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Typography,
} from "@material-ui/core";
import EmailText from "components/_refactor/text/email";
import PhoneText from "components/_refactor/text/phone";
import { Close, WifiCalling3 } from "@mui/icons-material";
import LoadingData from "components/Loading/LoadingData";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import {
  getEmailOrigemLabel,
  getStoreNotificationEmail,
  updateStoreNotificationEmail,
} from "lib/api/store-notification-email";

function normalizeEmail(value) {
  if (!value || typeof value !== "string") return "";
  return value.trim();
}

function getEmailSelectionKey(email, origem) {
  return `${origem || "email"}::${normalizeEmail(email).toLowerCase()}`;
}

function findEmailItemByAddress(emails, address) {
  const normalized = normalizeEmail(address).toLowerCase();
  if (!normalized) return null;
  return (
    emails.find(
      (item) => normalizeEmail(item.Email).toLowerCase() === normalized,
    ) ?? null
  );
}

export default function ContactCard({ storeId, setOpenedModal, setType }) {
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contact, setContact] = useState({});
  const [notificationConfig, setNotificationConfig] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState("");
  const snackbar = useSnackbar();
  const { getStores } = useStructureQueries();

  const availableEmails = notificationConfig?.EmailsDisponiveis ?? [];
  const configuredEmail = normalizeEmail(
    notificationConfig?.EmailNotificacaoBoleto,
  );
  const isSingleEmail = availableEmails.length === 1;
  const hasMultipleEmails = availableEmails.length > 1;
  const canSaveNotificationEmail =
    hasMultipleEmails &&
    !!selectedEmail &&
    normalizeEmail(selectedEmail).toLowerCase() !==
      configuredEmail.toLowerCase();

  const loadData = async () => {
    setSearching(true);
    try {
      const [storesResult, notificationResult] = await Promise.all([
        getStores({
          idPessoaLoja: storeId,
          idMarca: 1,
        }),
        getStoreNotificationEmail(storeId),
      ]);

      const list = storesResult?.data ?? [];
      setContact(list[0] ?? {});
      setNotificationConfig(notificationResult);

      const emails = notificationResult?.EmailsDisponiveis ?? [];
      const savedEmail = normalizeEmail(notificationResult?.EmailNotificacaoBoleto);
      const savedItem = findEmailItemByAddress(emails, savedEmail);
      setSelectedEmail(savedItem?.Email ?? emails[0]?.Email ?? "");
    } catch (error) {
      snackbar.showMessage(
        error instanceof Error
          ? error.message
          : "Erro ao buscar informações de contatos.",
        { severity: "error" },
      );
    } finally {
      setSearching(false);
    }
  };

  const handleSaveNotificationEmail = async () => {
    if (!selectedEmail) {
      snackbar.showMessage("Selecione um e-mail para notificação.", {
        severity: "warning",
      });
      return;
    }

    setSaving(true);
    try {
      const updated = await updateStoreNotificationEmail(storeId, selectedEmail);
      setNotificationConfig(updated);
      setSelectedEmail(updated?.EmailNotificacaoBoleto ?? selectedEmail);
      snackbar.showMessage("E-mail de notificação atualizado com sucesso!", {
        severity: "success",
      });
    } catch (error) {
      snackbar.showMessage(
        error instanceof Error
          ? error.message
          : "Erro ao salvar e-mail de notificação.",
        { severity: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  const contactInfo = [
    {
      label: "Telefone",
      value: <PhoneText phone={contact.FonePrincipal} />,
    },
    {
      label: "Celular",
      value: <PhoneText phone={contact.FoneCelular} />,
    },
    {
      label: "Telefone Recado",
      value: <PhoneText phone={contact.FoneRecado} />,
    },
    {
      label: "E-mail Contato",
      value: <EmailText email={contact.EmailPrincipal} />,
    },
    {
      label: "E-mail NFe",
      value: <EmailText email={contact.EmailNfe ?? contact.EmailNFE} />,
    },
    {
      label: "E-mail Alternativo",
      value: <EmailText email={contact.EmailAlternativo} />,
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  return (
    <Dialog open onClose={() => setOpenedModal(null)} maxWidth={"md"}>
      <DialogTitle>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="h3" color="primary">
            <WifiCalling3 style={{ marginRight: "12px" }} />
            Meus Contatos
          </Typography>
          <IconButton onClick={() => setOpenedModal(null)}>
            <Close />
          </IconButton>
        </div>
      </DialogTitle>

      {searching ? (
        <DialogContent>
          <LoadingData searching label="Buscando dados de contatos..." />
        </DialogContent>
      ) : (
        <DialogContent>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              {contactInfo.map(({ label, value }) => (
                <Typography key={label} variant="caption" component="div">
                  <strong>{label}:</strong> {value}
                </Typography>
              ))}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                E-mail para notificação de boletos
              </Typography>

              {availableEmails.length === 0 && (
                <Typography variant="caption" color="error">
                  Nenhum e-mail cadastrado para esta loja.
                </Typography>
              )}

              {isSingleEmail && (
                <Box>
                  <Typography variant="caption" component="div">
                    <strong>
                      {getEmailOrigemLabel(availableEmails[0].Origem)}:
                    </strong>{" "}
                    <EmailText email={availableEmails[0].Email} />
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    component="div"
                    style={{ marginTop: "0.5rem" }}
                  >
                    Este e-mail será utilizado para recebimento da notificação
                    dos boletos vencidos ou a vencer.
                  </Typography>
                </Box>
              )}

              {hasMultipleEmails && (
                <>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    component="div"
                    style={{ marginBottom: "0.75rem" }}
                  >
                    Selecione o e-mail que será utilizado para recebimento da
                    notificação dos boletos vencidos ou a vencer.
                  </Typography>
                  <RadioGroup
                    value={getEmailSelectionKey(
                      selectedEmail,
                      findEmailItemByAddress(availableEmails, selectedEmail)
                        ?.Origem,
                    )}
                    onChange={(e) => {
                      const [, email] = e.target.value.split("::");
                      setSelectedEmail(email);
                    }}
                  >
                    {availableEmails.map((item) => (
                      <FormControlLabel
                        key={getEmailSelectionKey(item.Email, item.Origem)}
                        value={getEmailSelectionKey(item.Email, item.Origem)}
                        control={<Radio color="primary" />}
                        label={
                          <Typography variant="caption">
                            <strong>{getEmailOrigemLabel(item.Origem)}:</strong>{" "}
                            {item.Email}
                          </Typography>
                        }
                      />
                    ))}
                  </RadioGroup>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
      )}

      <DialogActions>
        <Button
          variant="outlined"
          style={{ marginTop: "1.5rem" }}
          onClick={() => setOpenedModal(null)}
          disabled={saving}
        >
          Fechar
        </Button>
        {hasMultipleEmails && (
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: "1.5rem" }}
            disabled={saving || searching || !canSaveNotificationEmail}
            onClick={handleSaveNotificationEmail}
          >
            {saving ? "Salvando..." : "Salvar e-mail de notificação"}
          </Button>
        )}
        <Button
          variant="contained"
          color="primary"
          style={{ marginTop: "1.5rem" }}
          disabled={saving}
          onClick={() => {
            setOpenedModal("ALTERACAO");
            setType("CONTACT");
          }}
        >
          Solicitar alteração
        </Button>
      </DialogActions>
    </Dialog>
  );
}
